// © Microsoft Corporation. All rights reserved.

import { FluentButton } from "@fluentui/web-components";
import { DOM, FASTElement, observable } from "@microsoft/fast-element";
import { keyCodeEscape } from "@microsoft/fast-web-utilities";
import { MsnMarketSelectorProps } from "@msnews/msn-market-selector";
import { SettingsLocalizedStrings, SettingsTelemetryContext } from "./Interfaces";

export class Settings extends FASTElement {
    /** Flag to hide the market selector */
    @observable public hideMarketSelector: boolean;

    /** Localized strings object */
    @observable public localizedStrings: SettingsLocalizedStrings = {};

    /** Callback to navigate to "Personalize" */
    @observable public goToPersonalize?: () => void = () => {};

    /** Flag if the settings dialog should be visible */
    @observable public isSettingsDialogVisible = false;

    /** Settings telemetry context */
    @observable public telemetryContext: SettingsTelemetryContext = {};

    /** Market selector props */
    @observable public marketSelectorProps: MsnMarketSelectorProps = {} as MsnMarketSelectorProps;

    /** Selected market */
    @observable public selectedMarketId: string;

    /** Override styles */
    @observable public settingsOverrideStyles: string;

    /** Settings button ref */
    public settingsButton: FluentButton;

    /** Close button ref */
    public closeButton: FluentButton;

    /**
     * Sets the settings dialog visible state
     * @param isSettingsDialogVisible new isSettingsDialogVisible value
     */
    public toggleSettingsDialog = (isSettingsDialogVisible: boolean): void => {
        this.isSettingsDialogVisible = isSettingsDialogVisible;

        if (isSettingsDialogVisible) {
            document.addEventListener("keydown", this.onKeydown);
            this.setDialogPosition();

            if (this.closeButton) {
                DOM.queueUpdate(() => this.closeButton.focus());
            }
        } else {
            document.removeEventListener("keydown", this.onKeydown);
        }
    };

    /**
     * Callback for when the personalize settings button is clicked
     */
    public onPersonalizeClick = (): void => {
        this.toggleSettingsDialog(false);
        this.goToPersonalize();
    };

    /**
     * Handler for key down events
     * @param event keyboard event
     */
    private onKeydown = (event: KeyboardEvent): void => {
        if (event.keyCode === keyCodeEscape) {
            this.toggleSettingsDialog(false);
        }
    };

    /**
     * Helper for setting position styles for settings dialog
     */
    private setDialogPosition(): void {
        const refPosition = this.settingsButton.getBoundingClientRect();

        const verticalPosition = `top: ${refPosition.bottom}px;`;
        let horizontalPosition;
        if (document.dir === "rtl") {
            horizontalPosition = `left: ${refPosition.left}px`;
        } else {
            horizontalPosition = `left: ${refPosition.left + refPosition.width - 320}px`;
        }

        this.settingsOverrideStyles = `.settings-dialog::part(control) {position: absolute; ${verticalPosition} ${horizontalPosition}}`;
    }
}